import type { FormFieldDef } from '@hcc/types';
import { asDamageMarkup } from '@hcc/types';
import { GridFieldView } from './GridFieldView';
import { DamageMarkupField } from './DamageMarkupField';
import { DateSelect } from './DateSelect';
import { HELP_CLASS } from './fieldStyles';
import { StaticFieldView } from './StaticFieldView';
import { TextFieldView } from './TextFieldView';
import { OptionFieldView } from './OptionFieldView';
import { CompositeFieldView } from './CompositeFieldView';
import { FieldLabelText, FieldHelp, INVALID_GROUP_CLASS } from './fieldChrome';
import { asString, type FieldValue, type GridValue } from './formValues';

/**
 * Which part of a field failed validation, so the renderer can paint it red.
 * `whole` flags the entire control; `parts` names the offending address
 * sub-fields (line1 / city / postcode) when only some are missing.
 */
export interface FieldInvalid {
  whole?: boolean;
  parts?: string[];
}

interface FieldViewProps {
  field: FormFieldDef;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  disabled?: boolean;
  invalid?: FieldInvalid;
}

/**
 * Renders a single dynamic form field exactly as the inductee sees it. Pure
 * presentation: the caller owns the value. Dispatches to the per-family
 * views; shared by the builder live preview and the inductee FormRenderer.
 */
export function FieldView({ field, value, onChange, disabled, invalid }: FieldViewProps) {
  const { type } = field;
  const err = invalid?.whole === true;

  if (
    type === 'section_heading' ||
    type === 'paragraph' ||
    type === 'rich_text' ||
    type === 'image'
  ) {
    return <StaticFieldView field={field} />;
  }

  if (type === 'name' || type === 'address') {
    return (
      <CompositeFieldView
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        err={err}
        invalidParts={invalid?.parts}
      />
    );
  }

  if (type === 'dropdown' || type === 'radio' || type === 'yes_no' || type === 'checkbox') {
    return (
      <OptionFieldView field={field} value={value} onChange={onChange} disabled={disabled} err={err} />
    );
  }

  if (type === 'file_upload') {
    return (
      <label className="block text-sm">
        <FieldLabelText field={field} />
        <input
          type="file"
          disabled
          multiple={field.multiple}
          accept={(field.accept ?? []).map((e) => '.' + e).join(',')}
          className="mt-1.5 block w-full text-sm text-fg-muted file:mr-3 file:rounded-token-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:text-fg-muted"
        />
        <span className={HELP_CLASS}>
          {field.help ? field.help + ' ' : ''}
          Allowed: {(field.accept ?? []).join(', ') || 'any safe file type'}
        </span>
      </label>
    );
  }

  if (type === 'signature') {
    // The interactive pad lives in the inductee renderer; here (builder preview
    // and read-only contexts) show a placeholder so the layout is clear.
    return (
      <label className="block text-sm">
        <FieldLabelText field={field} />
        <div className="mt-1.5 flex h-28 items-center justify-center rounded-token-sm border border-dashed border-line-strong/30 bg-white text-xs text-slate-400">
          Signature pad (the inductee signs here)
        </div>
        <FieldHelp field={field} />
      </label>
    );
  }

  if (type === 'damage_markup') {
    // Read-only here (builder preview, HR review): the interactive drawing
    // surface lives in the inductee renderer, like the signature pad.
    return (
      <fieldset className="block text-sm">
        <FieldLabelText field={field} />
        <div className={err ? INVALID_GROUP_CLASS : ''}>
          <DamageMarkupField imageKey={field.image_key ?? ''} value={asDamageMarkup(value)} disabled />
        </div>
        <FieldHelp field={field} />
      </fieldset>
    );
  }

  if (type === 'grid' && field.grid) {
    return (
      <fieldset className="block text-sm">
        <FieldLabelText field={field} />
        <div className={err ? INVALID_GROUP_CLASS : ''}>
          <GridFieldView
            grid={field.grid}
            value={(value as GridValue) ?? {}}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
        <FieldHelp field={field} />
      </fieldset>
    );
  }

  if (type === 'date') {
    // Day/Month/Year selects instead of a native calendar so a year decades
    // back (a date of birth) is one pick, not endless month-scrolling.
    return (
      <fieldset className="block text-sm">
        <FieldLabelText field={field} />
        <DateSelect value={asString(value)} onChange={onChange} disabled={disabled} invalid={err} />
        <FieldHelp field={field} />
      </fieldset>
    );
  }

  // short_text / long_text / email / phone / number
  return (
    <TextFieldView field={field} value={value} onChange={onChange} disabled={disabled} err={err} />
  );
}
